/**
 * Crew Feed client API
 *
 * Thin authenticated helpers for pulse_cards used by Your crew + SpacePulse.
 * Do not inline `/api/pulse/*` fetch in UI — call these instead.
 *
 * Primary responsibilities:
 * - Fetch current pulse rows
 * - Record Love/save feedback and Go/Discuss clicks
 * - Get/set Crew interest brief via /api/pulse/preferences (F1)
 * - Get/set Crew briefing schedule via /api/pulse/preferences (F6)
 * - Get/set C3 Meet-your-Crew Mate draft via /api/pulse/preferences
 *   (meta.crew_member_draft — BOUND only with real Helper UUID)
 * - List Assistants as Helper bind candidates (GET /api/assistants)
 * - Get/set C4 Custom Rules draft via /api/pulse/preferences
 *   (meta.crew_custom_rules_draft — WIRED when Never strips live)
 * - Soft-read last interest silence via /api/pulse/interest-silence
 *   (emptySilencedHint — soft-fails to null; no Muse filler)
 * - Soft-copy throw / fallback bases (CREW_FEED_API_*) + default card
 *   limit — Metro mirrors in mobile/lib/crew/feed-api.ts (no web/ import)
 *
 * Dependencies:
 * - authenticatedFetch
 * - PulseCardLike
 * - interest-brief / briefing-schedule / member-bind / custom-rules extract helpers
 */

import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import {
  extractCrewBriefingSchedule,
  type CrewBriefingSchedule
} from '@/lib/crew/briefing-schedule'
import {
  crewCustomRulesDraftToJson,
  extractCrewCustomRulesDraft,
  normalizeCrewCustomRulesDraft,
  type CrewCustomRulesDraft
} from '@/lib/crew/custom-rules'
import type { PulseCardLike } from '@/lib/crew/feed-card'
import {
  CREW_INTEREST_BRIEF_MAX,
  extractCrewInterestBrief
} from '@/lib/crew/interest-brief'
import {
  crewMemberDraftToJson,
  extractCrewMemberDraft,
  normalizeCrewMemberDraft,
  type CrewMemberDraft
} from '@/lib/crew/member-bind'
import { CREW_FEED_SURFACE_DEFAULT } from '@/lib/crew/love-writeback-surfaces'
import type { CrewFeedSurface } from '@/lib/crew/love-writeback-surfaces'
import { extractCrewIdentity, type CrewIdentity } from '@/lib/crew/crew-name'

/**
 * Finite edition card cap — YourCrewHome / CrewFeedList / YourCrewScreen /
 * fetchCrewPulseCards default. Do not re-inline `8`.
 * Mobile mirrors the same constant (Metro cannot import web/).
 */
export const CREW_FEED_DEFAULT_CARD_LIMIT = 8

/** Soft-copy base — load pulse edition (status suffix via helper on web). */
export const CREW_FEED_API_LOAD_CARDS_ERROR = 'Could not'

/** Soft-copy base — Love / thumbs feedback write. */
export const CREW_FEED_API_SAVE_FEEDBACK_ERROR = 'Could not'

/** Soft-copy base — Go / Discuss click mark. */
export const CREW_FEED_API_MARK_CLICKED_ERROR = 'Could not'

/** Soft-copy base — interest whisper GET. */
export const CREW_FEED_API_LOAD_INTEREST_BRIEF_ERROR = 'Could not'

/** Soft-copy base — interest whisper PUT. */
export const CREW_FEED_API_SAVE_INTEREST_BRIEF_ERROR = 'Could not'

/** Soft-copy base — briefing schedule GET. */
export const CREW_FEED_API_LOAD_BRIEFING_SCHEDULE_ERROR =
  'Could not'

/** Soft-copy base — briefing schedule PUT. */
export const CREW_FEED_API_SAVE_BRIEFING_SCHEDULE_ERROR =
  'Could not'

/** Soft-copy base — C3 Mate draft GET. */
export const CREW_FEED_API_LOAD_CREW_MEMBER_DRAFT_ERROR =
  'Could not'

/** Soft-copy base — C3 Mate draft PUT. */
export const CREW_FEED_API_SAVE_CREW_MEMBER_DRAFT_ERROR =
  'Could not'

/** Soft-copy base — C4 Custom Rules draft GET. */
export const CREW_FEED_API_LOAD_CREW_CUSTOM_RULES_DRAFT_ERROR =
  'Could not'

/** Soft-copy base — C4 Custom Rules draft PUT. */
export const CREW_FEED_API_SAVE_CREW_CUSTOM_RULES_DRAFT_ERROR =
  'Could not'

/**
 * Soft-copy base — interest silence soft-read (mobile routeAuthJson
 * fallback; web soft-fails without throwing this string).
 */
export const CREW_FEED_API_LOAD_INTEREST_SILENCE_ERROR =
  'Could not'

/**
 * Lockstep mirror of feed-api soft-copy + default limit (web ↔ mobile).
 * Bun: `__tests__/lib/crew-feed-api-copy-parity.test.ts`.
 */
export const CREW_FEED_API_COPY_MIRROR = {
  CREW_FEED_DEFAULT_CARD_LIMIT,
  CREW_FEED_API_LOAD_CARDS_ERROR,
  CREW_FEED_API_SAVE_FEEDBACK_ERROR,
  CREW_FEED_API_MARK_CLICKED_ERROR,
  CREW_FEED_API_LOAD_INTEREST_BRIEF_ERROR,
  CREW_FEED_API_SAVE_INTEREST_BRIEF_ERROR,
  CREW_FEED_API_LOAD_BRIEFING_SCHEDULE_ERROR,
  CREW_FEED_API_SAVE_BRIEFING_SCHEDULE_ERROR,
  CREW_FEED_API_LOAD_CREW_MEMBER_DRAFT_ERROR,
  CREW_FEED_API_SAVE_CREW_MEMBER_DRAFT_ERROR,
  CREW_FEED_API_LOAD_CREW_CUSTOM_RULES_DRAFT_ERROR,
  CREW_FEED_API_SAVE_CREW_CUSTOM_RULES_DRAFT_ERROR,
  CREW_FEED_API_LOAD_INTEREST_SILENCE_ERROR
} as const

/** Web throw shape — base soft-copy + HTTP status (mobile uses base only). */
export function crewFeedApiStatusError(
  base: string,
  status: number
): string {
  return `${base} (${status})`
}

export {
  CREW_INTEREST_BRIEF_CHROME_MIRROR,
  CREW_INTEREST_BRIEF_COUNT_ID,
  CREW_INTEREST_BRIEF_COUNT_LIVE_REMAINING,
  CREW_INTEREST_BRIEF_FIELD_NAME,
  CREW_INTEREST_BRIEF_HINT,
  CREW_INTEREST_BRIEF_MAX,
  CREW_INTEREST_BRIEF_PLACEHOLDER,
  CREW_INTEREST_BRIEF_SAVE_A11Y_LABEL,
  CREW_INTEREST_BRIEF_SAVE_LABEL,
  CREW_INTEREST_BRIEF_TITLE,
  CREW_SAVE_BUSY_LABEL,
  CREW_WHISPER_CLEARED,
  CREW_WHISPER_LOAD_ERROR,
  CREW_WHISPER_SAVE_ERROR,
  CREW_WHISPER_SAVE_SUCCESS,
  CREW_WHISPER_TOAST_MIRROR,
  crewInterestBriefCountAriaLabel,
  crewInterestBriefCountShouldAnnounce,
  crewInterestBriefCountText,
  extractCrewInterestBrief,
  mergeCardTypePreferencesPatch,
  mergeCrewInterestBrief,
  resolveCardTypePreferencesUpdate
} from '@/lib/crew/interest-brief'

export {
  CREW_BRIEFING_CHROME_MIRROR,
  CREW_BRIEFING_CRON_ENTRYPOINT,
  CREW_BRIEFING_ENABLE_LABEL,
  CREW_BRIEFING_MONITOR_FIELD_LABEL,
  CREW_BRIEFING_MONITOR_FIELD_NAME,
  CREW_BRIEFING_MONITOR_MODE_CADENCE,
  CREW_BRIEFING_MONITOR_MODE_CADENCE_LABEL,
  CREW_BRIEFING_MONITOR_MODE_DEFAULT,
  CREW_BRIEFING_MONITOR_MODE_FIELD_LABEL,
  CREW_BRIEFING_MONITOR_MODE_FIELD_NAME,
  CREW_BRIEFING_MONITOR_MODE_GROUP_ID,
  CREW_BRIEFING_MONITOR_MODE_HINT,
  CREW_BRIEFING_MONITOR_MODE_HINT_ID,
  CREW_BRIEFING_MONITOR_MODE_ON_CHANGE,
  CREW_BRIEFING_MONITOR_MODE_ON_CHANGE_LABEL,
  CREW_BRIEFING_MONITOR_PLACEHOLDER,
  CREW_BRIEFING_MONITOR_QUERY_MAX,
  CREW_BRIEFING_SAVE_A11Y_LABEL,
  CREW_BRIEFING_SAVE_LABEL,
  CREW_BRIEFING_SCHEDULE_DEFAULT,
  CREW_BRIEFING_SCHEDULE_DUE_WINDOW_MINUTES,
  CREW_BRIEFING_SCHEDULE_FIELD_LABEL,
  CREW_BRIEFING_SCHEDULE_FIELD_NAME,
  CREW_BRIEFING_SCHEDULE_MAX,
  CREW_BRIEFING_SCHEDULE_PANEL_ID,
  CREW_BRIEFING_SCHEDULE_PLACEHOLDER,
  CREW_BRIEFING_SECTION_HIDE_LABEL,
  CREW_BRIEFING_SECTION_LABEL,
  CREW_BRIEFING_STATUS_DUE_NOW,
  CREW_BRIEFING_STATUS_ID,
  CREW_BRIEFING_STATUS_PAUSED,
  CREW_BRIEFING_STATUS_AUTO_PAUSED,
  CREW_BRIEFING_QUIET_STREAK_PREFIX,
  CREW_BRIEFING_EMPTY_SKIP_AUTO_PAUSE_AFTER,
  CREW_BRIEFING_STATUS_UNKNOWN,
  CREW_BRIEFING_NEXT_RUN_PREFIX,
  CREW_BRIEFING_NEXT_RUN_HORIZON_DAYS,
  CREW_BRIEFING_VALIDATE_BAD_EXPRESSION,
  CREW_BRIEFING_VALIDATE_INVALID,
  CREW_BRIEFING_VALIDATE_MIRROR,
  CREW_BRIEFING_VALIDATE_NOT_OBJECT,
  CREW_BRIEFING_VALIDATE_REQUIRES_MONITOR,
  CREW_BRIEFING_VALIDATE_REQUIRES_SCHEDULE,
  CREW_SCHEDULE_CLEARED,
  CREW_SCHEDULE_INCOMPLETE_TITLE,
  CREW_SCHEDULE_SAVE_ERROR,
  CREW_SCHEDULE_SAVE_SUCCESS,
  CREW_SCHEDULE_TOAST_MIRROR,
  CREW_SCHEDULED_DELETE_A11Y,
  CREW_SCHEDULED_DELETE_CONFIRM_ACTION,
  CREW_SCHEDULED_DELETE_CONFIRM_BODY,
  CREW_SCHEDULED_DELETE_CONFIRM_CANCEL,
  CREW_SCHEDULED_DELETE_CONFIRM_TITLE,
  CREW_SCHEDULED_DELETE_LABEL,
  CREW_SCHEDULED_EDIT_A11Y,
  CREW_SCHEDULED_EDIT_LABEL,
  CREW_SCHEDULED_LIST_ADD_A11Y,
  CREW_SCHEDULED_LIST_ADD_LABEL,
  CREW_SCHEDULED_LIST_EMPTY,
  CREW_SCHEDULED_LIST_HINT,
  CREW_SCHEDULED_LIST_MIRROR,
  CREW_SCHEDULED_LIST_REGION_ID,
  CREW_SCHEDULED_LIST_TITLE,
  CREW_SCHEDULED_LIST_UNTITLED,
  CREW_SCHEDULED_PAUSE_A11Y,
  CREW_SCHEDULED_PAUSE_LABEL,
  CREW_SCHEDULED_RESUME_A11Y,
  CREW_SCHEDULED_RESUME_AUTO_PAUSED_A11Y,
  CREW_SCHEDULED_RESUME_LABEL,
  applyCrewBriefingOnChangeEmptySkipLifecycle,
  crewBriefingScheduleRowTitle,
  crewScheduledPauseResumeA11y,
  extractCrewBriefingSchedule,
  extractEnabledMonitorQuery,
  formatCrewBriefingQuietStreakStatus,
  formatCrewBriefingScheduleStatus,
  isCrewBriefingScheduleAutoPaused,
  isCrewBriefingScheduleDue,
  isValidBriefingScheduleExpression,
  listCrewBriefingSchedules,
  mergeCrewBriefingSchedule,
  normalizeCrewBriefingEmptySkipCount,
  normalizeCrewBriefingSchedule,
  previewNextCrewBriefingRunAt,
  resolveCrewBriefingScheduleDueClock,
  resolveCrewLocalIanaTimeZone,
  resolveCrewFeedEmptyScheduleHint,
  resolveMiddayBriefingScheduleDue,
  validateCrewBriefingSchedule,
  withCrewBriefingScheduleEnabled,
  type CrewBriefingMonitorMode,
  type CrewBriefingSchedule,
  type CrewBriefingScheduleDueClock
} from '@/lib/crew/briefing-schedule'

type PulseCardsResponse = {
  cards?: PulseCardLike[]
}

type PulsePreferencesResponse = {
  preferences?: {
    interest_brief?: string
    briefing_schedule?: CrewBriefingSchedule | null
    crew_member_draft?: CrewMemberDraft | null
    crew_custom_rules_draft?: CrewCustomRulesDraft | null
    card_type_preferences?: unknown
  }
}

export type CrewFeedFeedbackType =
  | 'thumbs_up'
  | 'thumbs_down'
  | 'save'
  | 'share'
  | 'dismiss_positive'
  | 'dismiss_negative'

export {
  CREW_FEED_DEFAULT_SURFACE,
  CREW_FEED_SURFACES,
  CREW_FEED_SURFACE_DEFAULT,
  CREW_FEED_SURFACE_MIRROR,
  CREW_FEED_SURFACE_SPACE_PULSE,
  CREW_FEED_SURFACE_YOUR_CREW,
  CREW_LOVE_WRITEBACK_SURFACES,
  isCrewLoveWritebackSurface,
  type CrewFeedSurface,
  type CrewLoveWritebackSurface
} from '@/lib/crew/love-writeback-surfaces'

/**
 * Load the user's finite pulse edition.
 * Prefer no workspace filter for Your crew (personal overnight brief);
 * SpacePulse may pass workspaceId when scoping ops cards.
 */
export async function fetchCrewPulseCards(args?: {
  limit?: number
  workspaceId?: string | null
}): Promise<PulseCardLike[]> {
  const params = new URLSearchParams({
    limit: String(args?.limit ?? CREW_FEED_DEFAULT_CARD_LIMIT)
  })
  if (args?.workspaceId) {
    params.set('workspace_id', args.workspaceId)
  }

  const response = await authenticatedFetch(`/api/pulse/cards?${params}`, {
    method: 'GET',
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_LOAD_CARDS_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulseCardsResponse
  return Array.isArray(payload.cards) ? payload.cards : []
}

export async function postCrewFeedFeedback(args: {
  cardId: string
  feedbackType: CrewFeedFeedbackType
  surface?: CrewFeedSurface
  metadata?: Record<string, unknown>
}) {
  const response = await authenticatedFetch('/api/pulse/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      card_id: args.cardId,
      feedback_type: args.feedbackType,
      feedback_metadata: {
        surface: args.surface ?? CREW_FEED_SURFACE_DEFAULT,
        ...(args.metadata ?? {})
      }
    })
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_SAVE_FEEDBACK_ERROR,
        response.status
      )
    )
  }
}

/** Love — thumbs_up via shared /api/pulse/feedback (Your crew + list defaults). */
export async function loveCrewFeedCard(args: {
  cardId: string
  surface?: CrewFeedSurface
  metadata?: Record<string, unknown>
}) {
  return postCrewFeedFeedback({
    cardId: args.cardId,
    feedbackType: 'thumbs_up',
    surface: args.surface,
    metadata: args.metadata
  })
}

export async function markCrewFeedCardClicked(args: {
  cardId: string
  surface?: CrewFeedSurface
  actionData?: Record<string, unknown>
}) {
  const response = await authenticatedFetch('/api/pulse/cards', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'clicked',
      card_id: args.cardId,
      action_data: {
        surface: args.surface ?? CREW_FEED_SURFACE_DEFAULT,
        ...(args.actionData ?? {})
      }
    })
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_MARK_CLICKED_ERROR,
        response.status
      )
    )
  }
}

type InterestSilenceResponse = {
  silence_reason?: string | null
}

/**
 * Soft-read last interest research silence_reason from pulse_analytics
 * (generation_completed / midday_interest_refresh) via
 * GET /api/pulse/interest-silence. Optional workspaceId is forwarded for
 * route membership gating (analytics stay user-scoped). Never throws —
 * 401/403/5xx and network errors soft-fail to null so hosts leave
 * emptySilencedHint unset (no fake silence / Muse filler).
 */
export async function fetchCrewLastInterestSilenceReason(args?: {
  workspaceId?: string | null
}): Promise<string | null> {
  try {
    const params = new URLSearchParams()
    if (args?.workspaceId) {
      params.set('workspace_id', args.workspaceId)
    }
    const qs = params.toString()
    const response = await authenticatedFetch(
      `/api/pulse/interest-silence${qs ? `?${qs}` : ''}`,
      {
        method: 'GET',
        cache: 'no-store'
      }
    )
    // Unauthorized / forbidden / server soft-fail → null reason (not throw).
    if (!response.ok) return null
    const payload = (await response.json()) as InterestSilenceResponse
    const reason = payload?.silence_reason
    return typeof reason === 'string' && reason.trim() ? reason.trim() : null
  } catch {
    return null
  }
}

/** Load the user's Crew interest brief (whisper) from pulse preferences. */
export async function getCrewInterestBrief(): Promise<string> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'GET',
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_LOAD_INTEREST_BRIEF_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return ''

  if (typeof prefs.interest_brief === 'string') {
    return prefs.interest_brief
  }

  return extractCrewInterestBrief(prefs.card_type_preferences)
}

/**
 * Persist the Crew interest brief via PUT /api/pulse/preferences.
 * Client trims+caps only; server mergeCrewInterestBrief owns normalize.
 * Always read back the hydrated alias / nested meta — never soft-echo
 * the local trim (ZWSP-only paste must clear to '' from the server).
 */
export async function setCrewInterestBrief(interestBrief: string): Promise<string> {
  const trimmed = interestBrief.trim().slice(0, CREW_INTEREST_BRIEF_MAX)
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ interest_brief: trimmed })
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_SAVE_INTEREST_BRIEF_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (typeof prefs?.interest_brief === 'string') {
    return prefs.interest_brief
  }
  return extractCrewInterestBrief(prefs?.card_type_preferences)
}

/** Load the user's Crew briefing schedule from pulse preferences. */
export async function getCrewBriefingSchedule(): Promise<CrewBriefingSchedule | null> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'GET',
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_LOAD_BRIEFING_SCHEDULE_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.briefing_schedule != null) {
    return prefs.briefing_schedule
  }

  return extractCrewBriefingSchedule(prefs.card_type_preferences)
}

/**
 * Persist the Crew briefing schedule via PUT /api/pulse/preferences.
 * Pass null to clear the stored schedule.
 */
export async function setCrewBriefingSchedule(
  schedule: CrewBriefingSchedule | null
): Promise<CrewBriefingSchedule | null> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ briefing_schedule: schedule })
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_SAVE_BRIEFING_SCHEDULE_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.briefing_schedule !== undefined) {
    return prefs.briefing_schedule ?? null
  }

  return extractCrewBriefingSchedule(prefs.card_type_preferences)
}

/**
 * Load the user's C3 Meet-your-Crew Mate draft from pulse preferences.
 * BOUND only when helperId is a valid UUID — never invent a Helper bind.
 */
export async function getCrewMemberDraft(): Promise<CrewMemberDraft | null> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'GET',
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_LOAD_CREW_MEMBER_DRAFT_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.crew_member_draft != null) {
    return normalizeCrewMemberDraft(prefs.crew_member_draft)
  }

  return extractCrewMemberDraft(prefs.card_type_preferences)
}

/** Assistant row usable as a Helper bind candidate (C3). */
export type CrewBindHelperCandidate = {
  id: string
  name: string
}

/** Assistants catalog fetch for Helper bind — empty ≠ load-fail. */
export type CrewBindHelpersResult = {
  helpers: CrewBindHelperCandidate[]
  /** false when the Assistants request failed (not merely empty). */
  ok: boolean
}

/**
 * List workspace Assistants as Helper bind options.
 * `ok:false` → helpers=[] so hosts keep manual id (do not imply “you have none”).
 */
export async function listCrewBindHelpers(): Promise<CrewBindHelpersResult> {
  try {
    const response = await authenticatedFetch('/api/assistants', {
      method: 'GET',
      cache: 'no-store'
    })
    if (!response.ok) return { helpers: [], ok: false }
    const payload = (await response.json()) as {
      assistants?: Array<{ id?: unknown; name?: unknown }>
    }
    const rows = Array.isArray(payload.assistants) ? payload.assistants : []
    const out: CrewBindHelperCandidate[] = []
    for (const row of rows) {
      if (typeof row.id !== 'string' || !row.id.trim()) continue
      const name =
        typeof row.name === 'string' && row.name.trim()
          ? row.name.trim()
          : 'Assistant'
      out.push({ id: row.id.trim(), name })
    }
    return { helpers: out, ok: true }
  } catch {
    return { helpers: [], ok: false }
  }
}

/**
 * Persist the C3 Mate draft via PUT /api/pulse/preferences.
 * Pass null to clear. BOUND only when helperId is a valid UUID.
 * Always read back the hydrated alias — never soft-echo local draft.
 */
export async function setCrewMemberDraft(
  draft: CrewMemberDraft | null
): Promise<CrewMemberDraft | null> {
  const body =
    draft == null
      ? { crew_member_draft: null }
      : { crew_member_draft: crewMemberDraftToJson(draft) }

  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_SAVE_CREW_MEMBER_DRAFT_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.crew_member_draft !== undefined) {
    return prefs.crew_member_draft
      ? normalizeCrewMemberDraft(prefs.crew_member_draft)
      : null
  }

  return extractCrewMemberDraft(prefs.card_type_preferences)
}

/**
 * Load the user's C4 Custom Rules draft from pulse preferences.
 * Prefs stamp WIRED when isCrewCustomRulesEnforcementWired() (Never strips
 * live). Do not claim full GATE B from this alone.
 */
export async function getCrewCustomRulesDraft(): Promise<CrewCustomRulesDraft | null> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'GET',
    cache: 'no-store'
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_LOAD_CREW_CUSTOM_RULES_DRAFT_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.crew_custom_rules_draft != null) {
    return normalizeCrewCustomRulesDraft(prefs.crew_custom_rules_draft)
  }

  return extractCrewCustomRulesDraft(prefs.card_type_preferences)
}

/**
 * Persist the C4 Custom Rules draft via PUT /api/pulse/preferences.
 * Pass null to clear. Server stamps enforcementStatus via
 * crewCustomRulesEnforcementStamp() (WIRED when Never strips live).
 * Always read back the hydrated alias — never soft-echo local draft.
 */
export async function setCrewCustomRulesDraft(
  draft: CrewCustomRulesDraft | null
): Promise<CrewCustomRulesDraft | null> {
  const body =
    draft == null
      ? { crew_custom_rules_draft: null }
      : { crew_custom_rules_draft: crewCustomRulesDraftToJson(draft) }

  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw new Error(
      crewFeedApiStatusError(
        CREW_FEED_API_SAVE_CREW_CUSTOM_RULES_DRAFT_ERROR,
        response.status
      )
    )
  }

  const payload = (await response.json()) as PulsePreferencesResponse
  const prefs = payload.preferences
  if (!prefs) return null

  if (prefs.crew_custom_rules_draft !== undefined) {
    return prefs.crew_custom_rules_draft
      ? normalizeCrewCustomRulesDraft(prefs.crew_custom_rules_draft)
      : null
  }

  return extractCrewCustomRulesDraft(prefs.card_type_preferences)
}

// --------------
// Crew name (first-run naming on Your crew)
// --------------

export async function getCrewIdentity(): Promise<CrewIdentity> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'GET',
    cache: 'no-store'
  })
  if (!response.ok) {
    throw new Error(`Could not load crew name (${response.status})`)
  }
  const payload = (await response.json()) as PulsePreferencesResponse
  return extractCrewIdentity(payload.preferences?.card_type_preferences)
}

export async function setCrewIdentity(patch: {
  crewName?: string | null
  onboarded?: boolean
}): Promise<CrewIdentity> {
  const response = await authenticatedFetch('/api/pulse/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(patch.crewName !== undefined ? { crew_name: patch.crewName } : {}),
      ...(patch.onboarded !== undefined ? { crew_name_onboarded: patch.onboarded } : {})
    })
  })
  if (!response.ok) {
    throw new Error(`Could not save crew name (${response.status})`)
  }
  const payload = (await response.json()) as PulsePreferencesResponse
  return extractCrewIdentity(payload.preferences?.card_type_preferences)
}

// --------------
// Run 7 — owned crew (Nexus multi-crew)
// --------------

export type CrewOwnershipApiResponse = {
  mode: 'implicit_single_crew' | 'explicit_multi_crew'
  multiCrewEnabled: boolean
  crew: import('@/lib/crew/crew-ownership').OwnedCrew | null
}

export async function getOwnedCrew(
  workspaceId: string
): Promise<CrewOwnershipApiResponse> {
  const params = new URLSearchParams({ workspaceId })
  const response = await authenticatedFetch(
    `/api/crew/ownership?${params}`,
    { method: 'GET', cache: 'no-store' }
  )
  if (!response.ok) {
    throw new Error(`Could not load owned crew (${response.status})`)
  }
  return (await response.json()) as CrewOwnershipApiResponse
}

/**
 * Soft-read the most recent Crew Member Runner autonomy summary for the
 * workspace (what the overnight/midday run did / skipped / staged). Never
 * throws — 401/403/5xx soft-fail to null so the strip renders a pending
 * state (no invented run).
 */
export async function fetchCrewAutonomyRunSummary(args: {
  workspaceId: string
}): Promise<import('@/lib/crew/autonomy-run-summary').CrewAutonomyRunSummary | null> {
  try {
    const params = new URLSearchParams({ workspaceId: args.workspaceId })
    const response = await authenticatedFetch(
      `/api/crew/autonomy-summary?${params}`,
      { method: 'GET', cache: 'no-store' }
    )
    if (!response.ok) return null
    const payload = (await response.json()) as {
      summary?: unknown
    }
    const { parseCrewAutonomyRunSummary } = await import(
      '@/lib/crew/autonomy-run-summary'
    )
    return parseCrewAutonomyRunSummary(payload?.summary)
  } catch {
    return null
  }
}

export async function migrateOwnedCrewSalesAgent(input: {
  workspaceId: string
  name?: string
}): Promise<CrewOwnershipApiResponse> {
  return migrateOwnedCrewTemplate({
    workspaceId: input.workspaceId,
    templateId: 'sales-agent-second-shift',
    name: input.name
  })
}

export async function migrateOwnedCrewTemplate(input: {
  workspaceId: string
  templateId:
    | 'sales-agent-second-shift'
    | 'closers-room'
    | 'bd-outbound'
  name?: string
}): Promise<CrewOwnershipApiResponse> {
  const response = await authenticatedFetch('/api/crew/ownership', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      migrateTemplateId: input.templateId,
      ...(input.templateId === 'sales-agent-second-shift'
        ? { migrateSalesAgent: true }
        : {}),
      ...(input.name ? { name: input.name } : {})
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Could not create owned crew (${response.status})${detail ? `: ${detail}` : ''}`
    )
  }
  return (await response.json()) as CrewOwnershipApiResponse
}

/** Step-2 Space TEMPLATE bind — prefs migrate stays separate (ownershipWritesSpace: false). */
export async function bindCrewMemberTemplateSpace(input: {
  workspaceId: string
  templateId: 'sales-agent-second-shift' | 'closers-room' | 'bd-outbound'
  spaceId?: string
}): Promise<{
  ok: true
  templateId: string
  spaceId: string
  ownershipWritesSpace: false
}> {
  const response = await authenticatedFetch('/api/crew/template-bind', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      templateId: input.templateId,
      ...(input.spaceId ? { spaceId: input.spaceId } : {})
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Could not bind Space template (${response.status})${detail ? `: ${detail}` : ''}`
    )
  }
  return (await response.json()) as {
    ok: true
    templateId: string
    spaceId: string
    ownershipWritesSpace: false
  }
}

export async function patchOwnedCrewBudgetCaps(input: {
  workspaceId: string
  budgetCaps: Partial<import('@/lib/crew/crew-ownership').CrewBudgetCaps>
}): Promise<CrewOwnershipApiResponse> {
  const response = await authenticatedFetch('/api/crew/ownership', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      budgetCaps: input.budgetCaps
    })
  })
  if (!response.ok) {
    throw new Error(`Could not update budget caps (${response.status})`)
  }
  return (await response.json()) as CrewOwnershipApiResponse
}

/**
 * Full owned-crew PUT — rename, member delete, or replace members.
 * Prefer migrateOwnedCrewTemplate for adding TEMPLATE mates.
 */
export async function putOwnedCrew(input: {
  workspaceId: string
  crew: import('@/lib/crew/crew-ownership').OwnedCrew
}): Promise<CrewOwnershipApiResponse> {
  const response = await authenticatedFetch('/api/crew/ownership', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      crew: input.crew
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Could not update crew (${response.status})${detail ? `: ${detail}` : ''}`
    )
  }
  return (await response.json()) as CrewOwnershipApiResponse
}

// --------------
// Run 8 — permission grants + handoffs
// --------------

export type CrewPermissionGrantsApiResponse = {
  grants: import('@/lib/crew/crew-handoff').CrewPermissionGrant[]
  ownedCrewId: string | null
  semantics?: string
}

export type CrewHandoffsApiResponse = {
  handoffs: import('@/lib/crew/crew-handoff').CrewHandoff[]
  ownedCrewId: string | null
  handoffBudget?: {
    used: number
    cap: number
    period: string
    periodKey: string
    notesUsed?: number
    notesCap?: number
    tasksUsed?: number
    tasksCap?: number
  } | null
  /** Closer's Room morning prep — open Growth intake tasks for owned crew. */
  closersIntake?: Array<{
    id: string
    title: string
    description: string | null
    status: string
    priority: string | null
    createdAt: string | null
  }>
  /** True when intake list query failed — do not treat empty as "no work". */
  closersIntakeDegraded?: boolean
  /**
   * Critic85: peer crew meter when GET ?peerCrewId= is set (standing
   * auto-execute preflight). Same shape as handoffBudget; null if degraded.
   */
  receiverHandoffBudget?: {
    used: number
    cap: number
    period: string
    periodKey: string
    notesUsed?: number
    notesCap?: number
    tasksUsed?: number
    tasksCap?: number
  } | null
}

export async function listCrewPermissionGrantsApi(
  workspaceId: string
): Promise<CrewPermissionGrantsApiResponse> {
  const params = new URLSearchParams({ workspaceId })
  const response = await authenticatedFetch(
    `/api/crew/permission-grants?${params}`,
    { method: 'GET', cache: 'no-store' }
  )
  if (!response.ok) {
    throw new Error(`Could not load permission grants (${response.status})`)
  }
  return (await response.json()) as CrewPermissionGrantsApiResponse
}

export async function grantCrewPermissionInbound(input: {
  workspaceId: string
  fromCrewId: string
  allowedHandoffTypes: string[]
  requiresApproval?: boolean
  expiresAt?: string | null
}): Promise<{ grant: import('@/lib/crew/crew-handoff').CrewPermissionGrant }> {
  const response = await authenticatedFetch('/api/crew/permission-grants', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'grant',
      fromCrewId: input.fromCrewId,
      allowedHandoffTypes: input.allowedHandoffTypes,
      requiresApproval: input.requiresApproval ?? true,
      expiresAt: input.expiresAt ?? null
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Could not grant permission (${response.status})${detail ? `: ${detail}` : ''}`
    )
  }
  return (await response.json()) as {
    grant: import('@/lib/crew/crew-handoff').CrewPermissionGrant
  }
}

export async function revokeCrewPermissionApi(input: {
  workspaceId: string
  grantId: string
}): Promise<{
  grant: import('@/lib/crew/crew-handoff').CrewPermissionGrant
  expiredHandoffs?: number
}> {
  const response = await authenticatedFetch('/api/crew/permission-grants', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'revoke',
      grantId: input.grantId
    })
  })
  if (!response.ok) {
    throw new Error(`Could not revoke grant (${response.status})`)
  }
  return (await response.json()) as {
    grant: import('@/lib/crew/crew-handoff').CrewPermissionGrant
    expiredHandoffs?: number
  }
}

export async function listCrewHandoffsApi(
  workspaceId: string,
  opts?: { peerCrewId?: string | null }
): Promise<CrewHandoffsApiResponse> {
  const params = new URLSearchParams({ workspaceId })
  const peer = opts?.peerCrewId?.trim()
  if (peer) params.set('peerCrewId', peer)
  const response = await authenticatedFetch(`/api/crew/handoffs?${params}`, {
    method: 'GET',
    cache: 'no-store'
  })
  if (!response.ok) {
    throw new Error(`Could not load handoffs (${response.status})`)
  }
  return (await response.json()) as CrewHandoffsApiResponse
}

export async function completeClosersIntakeApi(input: {
  workspaceId: string
  taskId: string
}): Promise<{ ok: true; taskId: string }> {
  const response = await authenticatedFetch('/api/crew/handoffs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'complete_intake',
      taskId: input.taskId
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Could not complete intake (${response.status})${detail ? `: ${detail}` : ''}`
    )
  }
  return (await response.json()) as { ok: true; taskId: string }
}

export async function listCrewPeersApi(
  workspaceId: string
): Promise<{
  peers: Array<{ userId: string; crewId: string; crewName: string }>
  multiCrewEnabled: boolean
}> {
  const params = new URLSearchParams({ workspaceId })
  const response = await authenticatedFetch(`/api/crew/peers?${params}`, {
    method: 'GET',
    cache: 'no-store'
  })
  if (!response.ok) {
    throw new Error(`Could not load crew peers (${response.status})`)
  }
  return (await response.json()) as {
    peers: Array<{ userId: string; crewId: string; crewName: string }>
    multiCrewEnabled: boolean
  }
}

type CrewHandoffEffects = {
  crmOk: boolean | null
  taskOk: boolean | null
}

export async function createCrewHandoffApi(input: {
  workspaceId: string
  toCrewId: string
  type: string
  payload?: Record<string, unknown>
}): Promise<{
  handoff: import('@/lib/crew/crew-handoff').CrewHandoff
  approvalCardId: string | null
  effects?: CrewHandoffEffects | null
}> {
  const response = await authenticatedFetch('/api/crew/handoffs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'create',
      toCrewId: input.toCrewId,
      type: input.type,
      payload: input.payload ?? {}
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    let message = detail
    try {
      const parsed = JSON.parse(detail) as { error?: string }
      if (typeof parsed?.error === 'string' && parsed.error.trim()) {
        message = parsed.error.trim()
      }
    } catch {
      /* keep raw detail */
    }
    throw new Error(
      `Could not create handoff (${response.status})${message ? `: ${message}` : ''}`
    )
  }
  return (await response.json()) as {
    handoff: import('@/lib/crew/crew-handoff').CrewHandoff
    approvalCardId: string | null
    effects?: CrewHandoffEffects | null
  }
}

export async function approveCrewHandoffApi(input: {
  workspaceId: string
  handoffId: string
}): Promise<{
  handoff: import('@/lib/crew/crew-handoff').CrewHandoff
  effects?: CrewHandoffEffects | null
}> {
  const response = await authenticatedFetch('/api/crew/handoffs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'approve',
      handoffId: input.handoffId
    })
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    let message = detail
    try {
      const parsed = JSON.parse(detail) as { error?: string }
      if (typeof parsed?.error === 'string' && parsed.error.trim()) {
        message = parsed.error.trim()
      }
    } catch {
      /* keep raw detail */
    }
    throw new Error(
      `Could not approve handoff (${response.status})${message ? `: ${message}` : ''}`
    )
  }
  return (await response.json()) as {
    handoff: import('@/lib/crew/crew-handoff').CrewHandoff
    effects?: CrewHandoffEffects | null
  }
}

export async function rejectCrewHandoffApi(input: {
  workspaceId: string
  handoffId: string
}): Promise<{ handoff: import('@/lib/crew/crew-handoff').CrewHandoff }> {
  const response = await authenticatedFetch('/api/crew/handoffs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: input.workspaceId,
      action: 'reject',
      handoffId: input.handoffId
    })
  })
  if (!response.ok) {
    throw new Error(`Could not reject handoff (${response.status})`)
  }
  return (await response.json()) as {
    handoff: import('@/lib/crew/crew-handoff').CrewHandoff
  }
}
